import React, { useState, useEffect } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  RadialLinearScale,
  Title,
  Tooltip as ChartTooltip,
  Legend as ChartLegend,
  Filler
} from 'chart.js';
import LicenseGate from '../components/ui/LicenseGate';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import EmptyState from '../components/ui/EmptyState';
import ErrorMessage from '../components/ui/ErrorMessage';
import { ChartGuideModal } from '../components/ui';
import useChartGuide from '../hooks/useChartGuide';
import { useBrands, useBranches } from '../hooks/useMainPerformanceQueries';
import apiClient from '../utils/apiClient';
import config from '../config/api.config';
import { logger } from '../utils/logger';

// Import the new refactored components
import {
  KPIsSection,
  MonthlySalesComparison,
  CustomerSatisfactionComparison,
  SalesTrend,
  CustomerSatisfactionAnalysis,
  ProductCategorization,
  SmsChargeMonthlyTable
} from '../components/mainPerformance';
import { CustomerTrendsWithBalanceChart } from '../components/customerAnalytics';
import { getTimeRangeMonths } from '../utils/dataHelpers';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  RadialLinearScale,
  Title,
  ChartTooltip,
  ChartLegend,
  Filler
);

// API Services URL for connection check
const API_BASE_URL = `${config.API_URL}`;

interface MainPerformanceDashboardProps {
  brandId?: number;
  branchId?: number;
  timeRange?: string;
}

const MainPerformanceDashboard: React.FC<MainPerformanceDashboardProps> = ({
  brandId,
  branchId,
  timeRange = 'last6Months'
}) => {
  const { isModalOpen, currentGuide, closeGuide } = useChartGuide();

  // State for filters
  const [selectedBrand, setSelectedBrand] = useState<number | null>(brandId || null);
  const [selectedBranch, setSelectedBranch] = useState<number | null>(branchId || null);
  const [apiConnected, setApiConnected] = useState(false);
  const [globalError, setGlobalError] = useState<string | null>(null);
  
  // React Query hooks for brands and branches
  const { data: brands = [], isLoading: brandsLoading } = useBrands();
  const { data: branches = [], isLoading: branchesLoading } = useBranches(selectedBrand);
  
  // Check API connection on mount
  useEffect(() => {
    const checkApiConnection = async () => {
      try {
        await apiClient.get('/health-check');
        setApiConnected(true);
      } catch (error) {
        logger.error('API connection error', 'MainPerformanceDashboard', error);
        setApiConnected(false);
      }
    };
    
    checkApiConnection();
  }, []);
  
  // Set initial brand selection when brands are loaded
  useEffect(() => {
    if (brands.length > 0 && brandId && !selectedBrand) {
      const brand = brands.find(b => b.id === brandId);
      if (brand) {
        setSelectedBrand(brand.id);
      }
    }
  }, [brands, brandId, selectedBrand]);
  
  // Set initial branch selection when branches are loaded
  useEffect(() => {
    if (branches.length > 0) {
      if (branchId && !selectedBranch) {
        const branch = branches.find(b => b.id === branchId);
        if (branch) {
          setSelectedBranch(branch.id);
        }
      } else if (!selectedBranch && branches.length === 1) {
        // Auto-select if only one branch
        setSelectedBranch(branches[0].id);
      }
    }
  }, [branches, branchId, selectedBranch]);
  
  // Update selected branch when branchId prop changes
  useEffect(() => {
    if (branchId !== selectedBranch) {
      setSelectedBranch(branchId || null);
    }
  }, [branchId]);
  
  // Update selected brand when brandId prop changes
  useEffect(() => {
    if (brandId !== selectedBrand) {
      setSelectedBrand(brandId || null);
    }
  }, [brandId]);

  // Global error handler for all sections
  const handleSectionError = (error: Error) => {
    logger.error('Section error', 'MainPerformanceDashboard', error);
    // Could set a global error state if needed, or handle per-section
    // For now, let each component handle its own errors
  };

  // Render loading state only when actually loading data
  if ((brandsLoading) || (branchesLoading && selectedBrand)) {
    return (
      <div className="container-dashboard" dir="rtl">
        <div className="flex flex-col items-center justify-center min-h-[300px]" role="status" aria-live="polite">
          <LoadingSpinner
            variant="minimal"
            size="lg"
            message="در حال بارگذاری داده‌های داشبورد…"
          />
          <p className="mt-4 text-sm content-secondary">لطفاً چند لحظه صبر کنید…</p>
        </div>
      </div>
    );
  }

  // Check if no branch is selected
  if (!selectedBranch) {
    return (
      <div className="container-dashboard" dir="rtl">
        <div className="flex flex-col items-center justify-center min-h-[300px]">
          <div className="text-lg text-content-secondary">برای دیدن این داشبورد، ابتدا شعبه را انتخاب کنید.</div>
        </div>
      </div>
    );
  }

  // Render global error if exists
  if (globalError) {
    return (
      <div className="container-dashboard" dir="rtl">
        <ErrorMessage 
          message={globalError}
          onRetry={() => setGlobalError(null)}
        />
      </div>
    );
  }

  // Render main dashboard UI using the new components
  return (
    <LicenseGate licenseType="club-or-evaluation" dashboardName="داشبورد عملکرد کلی">
      <div className="container-dashboard">
        <div className="space-y-8 md:space-y-10 animate-enter-up">
          <h1 className="title-dashboard">
            عملکرد کلی
          </h1>

          {/* KPI Cards Section */}
          <div id="section-kpis" className="dashboard-section-anchor">
            <KPIsSection
              branchId={selectedBranch}
              timeRange={timeRange}
              onError={handleSectionError}
            />
          </div>

          

          {/* Sales and Satisfaction Comparison */}
          {/* <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <MonthlySalesComparison
              branchId={selectedBranch}
              timeRange={timeRange}
              onError={handleSectionError}
            />
            
            <CustomerSatisfactionComparison
              branchId={selectedBranch}
              timeRange={timeRange}
              onError={handleSectionError}
            />
          </div> */}
          
          {/* Satisfaction and Product Analysis */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div id="section-customer-satisfaction-analysis" className="dashboard-section-anchor">
              <CustomerSatisfactionAnalysis
                branchId={selectedBranch}
                timeRange={timeRange}
                onError={handleSectionError}
              />
            </div>

            <div id="section-sms-charge-monthly-table" className="dashboard-section-anchor">
              <SmsChargeMonthlyTable
                branchId={selectedBranch}
                timeRange={timeRange}
                onError={handleSectionError}
              />
            </div>
          </div>


          
          

          {/* Customer Trends with Balance */}
          <div id="section-customer-trends-balance-chart" className="dashboard-section-anchor">
            <CustomerTrendsWithBalanceChart
              branchId={selectedBranch}
              months={getTimeRangeMonths(timeRange)}
              title="تحلیل حفظ مشتریان"
              description="تحلیل نرخ بازگشت و وفاداری مشتریان"
              onError={handleSectionError}
            />
          </div>

          <div id="section-sales-trend" className="dashboard-section-anchor">
            <SalesTrend
              branchId={selectedBranch}
              timeRange={timeRange}
              onError={handleSectionError}
            />
          </div>

        </div>
        
        {/* Chart Guide Modal */}
        {currentGuide && (
          <ChartGuideModal
            isOpen={isModalOpen}
            onClose={closeGuide}
            title={currentGuide.title}
          >
            <div className="space-y-8">
              <div>
                <h3 className="text-lg sm:text-xl font-semibold text-text-primary mb-2">توضیحات</h3>
                <p className="text-text-secondary">{currentGuide.description}</p>
              </div>
              <div>
                <h3 className="text-lg sm:text-xl font-semibold text-text-primary mb-2">نحوه تفسیر</h3>
                <p className="text-text-secondary">{currentGuide.interpretation}</p>
              </div>
              {currentGuide.tips.length > 0 && (
                <div>
                  <h3 className="text-lg sm:text-xl font-semibold text-text-primary mb-2">نکات کلیدی</h3>
                  <ul className="list-disc list-inside space-y-2">
                    {currentGuide.tips.map((tip, index) => (
                      <li key={index} className="text-text-secondary">{tip}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </ChartGuideModal>
        )}
      </div>
    </LicenseGate>
  );
};

export default MainPerformanceDashboard;